/* eslint complexity: ["error", {max: 10}] -- Due to extra conditions */
import Avatar from "@/ui/core/Avatar";
import Text from "@/ui/core/Text";
import { HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import {
  joinTestIDs,
  listWithParentheses,
  toSentenceCase,
} from "@/utils/string.util";

type OverflowBehavior = "ellipsize" | "wrap";

interface AvatarCardProps extends BaseUserAvatarProps {
  isAppUser?: boolean;
  isHorizontal?: boolean;
  overflowBehavior?: OverflowBehavior;
  pronouns?: string[];
  shouldShowSubtitle?: boolean;
  subtitle?: string;
}

interface GetSubtitleArgs {
  isAppUser?: boolean;
  subtitle?: string;
  userType: string;
}

const getSubtitle = ({ isAppUser, subtitle, userType }: GetSubtitleArgs) => {
  return isAppUser ? "You" : subtitle || toSentenceCase(userType);
};

interface OverflowProps {
  flexShrinkHStackItems: number | undefined;
  flexShrinkVStack: number | undefined;
  flexVStack: number | undefined;
  flexWrapHStack: "wrap" | undefined;
  numberOfLines: number | undefined;
  shouldShowTrailingEllipses: boolean | undefined;
}

const getOverflowProps = (
  overflowBehavior?: OverflowBehavior,
): OverflowProps => {
  if (overflowBehavior === "ellipsize") {
    return {
      flexShrinkHStackItems: 1,
      flexShrinkVStack: undefined,
      flexVStack: 1,
      flexWrapHStack: undefined,
      numberOfLines: 1,
      shouldShowTrailingEllipses: true,
    };
  }
  if (overflowBehavior === "wrap") {
    return {
      flexShrinkHStackItems: undefined,
      flexShrinkVStack: 1,
      flexVStack: undefined,
      flexWrapHStack: "wrap",
      numberOfLines: undefined,
      shouldShowTrailingEllipses: undefined,
    };
  }
  return {
    flexShrinkHStackItems: undefined,
    flexShrinkVStack: undefined,
    flexVStack: undefined,
    flexWrapHStack: undefined,
    numberOfLines: undefined,
    shouldShowTrailingEllipses: undefined,
  };
};

const AvatarCard = ({
  isAppUser,
  isHorizontal = false,
  // by default we resort to truncating with trailing ellipses,
  // to not unintentionally disrupt UX/push things in expected ways
  overflowBehavior = "ellipsize",
  pronouns,
  shouldShowSubtitle = true,
  subtitle,
  userId,
  userName,
  userType,
}: AvatarCardProps) => {
  const baseTestID = joinTestIDs("avatar-card", userId);

  const subtitleValue = getSubtitle({ isAppUser, subtitle, userType });

  const {
    flexShrinkHStackItems,
    flexShrinkVStack,
    flexVStack,
    flexWrapHStack,
    numberOfLines,
    shouldShowTrailingEllipses,
  } = getOverflowProps(overflowBehavior);

  return (
    <HStack
      alignItems="center"
      gap={12}
      testID={joinTestIDs(baseTestID, "card")}
    >
      <Avatar
        size={isHorizontal ? "xs" : "sm"}
        testID={baseTestID}
        userId={userId}
        userName={userName}
        userType={userType}
      />
      <VStack flex={flexVStack} flexShrink={flexShrinkVStack} gap={4}>
        <HStack alignItems="center" flexWrap={flexWrapHStack} gap={6}>
          <Text
            // no flexShrink as the user's name should never vanish if not enough space
            numberOfLines={numberOfLines}
            shouldShowTrailingEllipses={shouldShowTrailingEllipses}
            testID={joinTestIDs(baseTestID, "name")}
            variant="bodyMedium"
          >
            {userName}
          </Text>
          {pronouns && (
            <Text
              // flexShrink since pronouns should vanish if not enough space after user's name
              flexShrink={flexShrinkHStackItems}
              numberOfLines={numberOfLines}
              shouldShowTrailingEllipses={shouldShowTrailingEllipses}
              testID={joinTestIDs(baseTestID, "pronouns")}
              variant="caption"
            >
              {listWithParentheses(pronouns)}
            </Text>
          )}
          {isHorizontal && shouldShowSubtitle && (
            <Text
              color={ColorToken.NeutralSecondary}
              // flexShrink since horizontal subtitle should vanish if not enough space after user's name
              flexShrink={flexShrinkHStackItems}
              numberOfLines={numberOfLines}
              shouldShowTrailingEllipses={shouldShowTrailingEllipses}
              testID={joinTestIDs(baseTestID, "type")}
              variant="caption"
            >
              {subtitleValue}
            </Text>
          )}
        </HStack>
        {!isHorizontal && shouldShowSubtitle && (
          <Text
            // no flexShrink as subtitles on their own line should never vanish if not enough space
            color={ColorToken.NeutralSecondary}
            numberOfLines={numberOfLines}
            shouldShowTrailingEllipses={shouldShowTrailingEllipses}
            testID={joinTestIDs(baseTestID, "type")}
            variant="caption"
          >
            {subtitleValue}
          </Text>
        )}
      </VStack>
    </HStack>
  );
};

export type { AvatarCardProps };
export default AvatarCard;
